import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials } from '@/mocks/movies';

const SUBMIT_URL = 'https://readdy.ai/api/form/d9odj7kit35ii3jdm6dg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotEl) {
        formData.delete('phone_alt');
      }

      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Organization"
        data={{
          name: '萝卜影视',
          url: 'https://luobo-movies.com',
          description: '萝卜影视汇聚电影大全，最新电影上线与热门电影排行榜，电影在线观看免费高清',
          foundingDate: '2022',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: 'contact@luobo-movies.com',
            availableLanguage: ['Chinese'],
          },
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于萝卜影视 - 品牌介绍与联系方式',
          description: '了解萝卜影视的品牌故事、用户口碑与联系方式。萝卜影视致力于为用户提供优质的免费在线观影体验。',
          url: 'https://luobo-movies.com/about',
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://luobo-movies.com/' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20film%20reel%20and%20projector%20in%20cozy%20vintage%20room%2C%20soft%20golden%20lighting%2C%20old%20movie%20posters%20on%20wall%2C%20nostalgic%20cinema%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20artistic%20composition%2C%20film%20photography%20aesthetic&width=1600&height=800&seq=about-hero&orientation=landscape"
              alt="萝卜影视"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
                关于萝卜影视
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-lg">
                用心做好每一个观影瞬间
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                    <p>
                      萝卜影视成立于2022年，是一家专注于为用户提供优质在线观影体验的影视平台。我们的使命是让每一位电影爱好者都能轻松便捷地找到自己喜欢的电影，享受高清流畅的观影体验。
                    </p>
                    <p>
                      自成立以来，萝卜影视始终坚持「内容为王、用户至上」的核心理念，持续扩充电影片库，优化播放技术。目前平台已收录超过上万部国内外优质电影资源，涵盖动作、喜剧、科幻、爱情、悬疑、动画、战争、纪录片等多种类型。
                    </p>
                    <p>
                      我们拥有一支热爱电影的团队，无论是经典佳片还是最新热映大片，萝卜影视都会第一时间为用户呈现。我们深知，每一部电影背后都有它的故事和情感，而我们希望成为连接电影与观众的桥梁。
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    我们的优势
                  </h2>
                  <div className="space-y-4">
                    {[
                      { icon: 'ri-movie-line', title: '海量片库', desc: '收录上万部国内外电影，涵盖多种类型' },
                      { icon: 'ri-speed-up-line', title: '极速播放', desc: '先进的视频加速技术，高清不缓冲' },
                      { icon: 'ri-device-line', title: '多端适配', desc: '电脑、平板、手机全设备流畅观影' },
                      { icon: 'ri-24-hours-line', title: '每日更新', desc: '最新电影第一时间上线，永不错过好片' },
                      { icon: 'ri-shield-check-line', title: '免费观影', desc: '无需注册无需付费，打开即看' },
                      { icon: 'ri-thumb-up-line', title: '精准推荐', desc: '热门排行实时更新，发现你的下一部好片' },
                    ].map((item) => (
                      <div key={item.title} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-primary-600`}></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                          <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100/50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                来自影迷朋友们的真实评价
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.name} className="bg-background-50 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      「{t.content}」
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议，欢迎随时与我们联系
              </p>

              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-100/50 rounded-lg p-6 md:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-honeypot"
                />

                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-full font-medium text-sm whitespace-nowrap transition-all duration-200 cursor-pointer"
                  >
                    {submitting ? '提交中...' : '发送留言'}
                  </button>
                  {formStatus === 'success' && (
                    <span className="text-sm text-emerald-600 font-medium whitespace-nowrap">
                      提交成功！我们会尽快回复您
                    </span>
                  )}
                  {formStatus === 'error' && (
                    <span className="text-sm text-red-500 font-medium whitespace-nowrap">
                      {formError}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}